
import { useState, useEffect, useCallback } from 'react';
import { Comment } from '@/types/social';
import { useCommentVotesRealtime } from './useCommentVotesRealtime';
import { fetchVoteCounts, voteForComment } from './utils/voteOperations';
import { ContentType } from '@/services/comments/types';

interface UseCommentVotesProps {
  comments: Comment[];
  userId: string | null;
  anonymousId: string | null;
  contentType: ContentType;
}

/**
 * Hook for managing comment votes
 */
export const useCommentVotes = ({
  comments,
  userId,
  anonymousId,
  contentType
}: UseCommentVotesProps) => {
  // State for votes and user interactions
  const [userVotes, setUserVotes] = useState<Record<string, { up?: boolean; down?: boolean }>>({});
  const [voteCounts, setVoteCounts] = useState<Record<string, number>>({});
  const [upvoteCounts, setUpvoteCounts] = useState<Record<string, number>>({});
  const [downvoteCounts, setDownvoteCounts] = useState<Record<string, number>>({});
  const [isVoting, setIsVoting] = useState<Record<string, boolean>>({});
  const [maxVotes, setMaxVotes] = useState<number>(0);
  
  // Extract comment IDs for realtime subscription
  const commentIds = comments.map(comment => comment.id);
  
  // Handle vote count changes from realtime updates - memoize to prevent unnecessary re-renders
  const handleVotesChange = useCallback((commentId: string, voteCount: number, upCount: number, downCount: number) => {
    console.log('Real-time: Vote count updated', commentId, voteCount, upCount, downCount);
    setVoteCounts(prevCounts => ({
      ...prevCounts,
      [commentId]: voteCount
    }));
    setUpvoteCounts(prevCounts => ({
      ...prevCounts,
      [commentId]: upCount
    }));
    setDownvoteCounts(prevCounts => ({
      ...prevCounts,
      [commentId]: downCount
    }));
  }, []);
  
  // Subscribe to vote changes in realtime
  useCommentVotesRealtime({
    contentType,
    commentIds,
    onVotesChange: handleVotesChange
  });

  // Fetch initial vote counts and user votes
  const fetchInitialVotes = useCallback(async () => {
    await fetchVoteCounts(
      comments,
      contentType,
      userId,
      anonymousId,
      { 
        setVoteCounts, 
        setUpvoteCounts, 
        setDownvoteCounts, 
        setUserVotes, 
        setMaxVotes 
      }
    );
  }, [comments, userId, anonymousId, contentType]);
  
  useEffect(() => {
    fetchInitialVotes();
  }, [fetchInitialVotes]);
  
  // Vote handler - memoize to prevent unnecessary re-renders
  const handleVote = useCallback(async (commentId: string, voteType: 'up' | 'down' = 'up') => {
    await voteForComment(
      commentId,
      voteType,
      contentType,
      userId,
      anonymousId,
      { userVotes, isVoting },
      { setUserVotes, setVoteCounts, setUpvoteCounts, setDownvoteCounts, setIsVoting }
    );
  }, [userVotes, isVoting, contentType, userId, anonymousId]);
  
  return {
    userVotes,
    voteCounts,
    upvoteCounts,
    downvoteCounts,
    isVoting,
    handleVote,
    maxVotes
  };
};
